import React, { Suspense, lazy } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, CircularProgress, Box } from '@mui/material';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import Login from './Pages/Login';
import NotFound from './Pages/NotFound';
import { AuthProvider } from './Context/AuthContext';
import ProtectedRoute from './Components/ProtectedRoute';
import Layout from './Components/Layout';

// Lazy load pages (code splitting — charge uniquement au moment de la navigation)
const Dashboard = lazy(() => import('./Pages/Dashboard'));
const GestionUsers = lazy(() => import('./Pages/GestionUsers'));
const Tarification = lazy(() => import('./Layout/Transport/Tarification'));
const Voyages = lazy(() => import('./Layout/Transport/Voyages'));
const Maintenance = lazy(() => import('./Layout/Transport/Maintenance'));
const Chauffeurs = lazy(() => import('./Layout/Transport/Chauffeurs'));
const Vehicules = lazy(() => import('./Layout/Transport/Vehicules'));
const TestPage = lazy(() => import('./Pages/testing'));
const NotAuthorized = lazy(() => import('./Pages/NotAuthorized'));
const Profile = lazy(() => import('./Pages/Profile'));
const Clients = lazy(() => import('./Layout/Clients/Clients'));
const Carburant = lazy(() => import('./Layout/Carburant'));
const ComingSoon = lazy(() => import('./Pages/isComing'));
const FacturesList = lazy(() => import('./Layout/Factures/FacturesList'));
const FactureView = lazy(() => import('./Layout/Factures/FactureView'));
const FactureForm = lazy(() => import('./Layout/Factures/FactureForm'));
const StockScelles = lazy(() => import('./Layout/Logistique/StockScelles'));
const DocumentManagement = lazy(() => import('./Layout/Documents/DocumentManagement'));
const DocumentStatistics = lazy(() => import('./Layout/Documents/DocumentStatistics'));
const BilanCarbon = lazy(() => import('./Pages/BilanCarbon'));
const CarbonDashboard = lazy(() => import('./Layout/BilanCarbon/CarbonLogistiqueDashboard'));
const CarbonAnalyse = lazy(() => import('./Layout/BilanCarbon/CarbonAnalyse'));
const CarbonSolutionsAction = lazy(() => import('./Layout/BilanCarbon/CarbonSolutionsAction'));
const ClientGroupsList = lazy(() => import('./Layout/ClientGroups/ClientGroupsList'));
const ClientGroupDetail = lazy(() => import('./Layout/ClientGroups/ClientGroupDetail'));
const FleetTracking = lazy(() => import('./Pages/FleetTracking'));
const ClientDashboard = lazy(() => import('./Pages/ClientDashboard'));

const PageFallback = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
    <CircularProgress />
  </Box>
);

const APP_ZOOM = 0.95;

const theme = createTheme({
  typography: { fontFamily: 'Montserrat, sans-serif' },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        'html, body, #root': {
          zoom: APP_ZOOM,                // Chromium browsers
          margin: 0,
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
          height: '100%',
          minHeight: '100%',
        },
        // Firefox fallback (no native zoom support)
        '@supports not (zoom: 1)': {
          'html, body, #root': {
            transform: `scale(${APP_ZOOM})`,
            transformOrigin: 'top left',
            width: `${100 / APP_ZOOM}%`,
            height: `${100 / APP_ZOOM}%`,
            overflowX: 'hidden',
          },
        },
      },
    },
  },
});

function App() {
  return (
    <Router>
      <AuthProvider>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Routes>
            {/* Public */}
            <Route path="/" element={<Login />} />
            <Route path="/testing" element={<Suspense fallback={<PageFallback />}><TestPage /></Suspense>} />
            <Route path="/NotAuthorized" element={<Suspense fallback={<PageFallback />}><NotAuthorized /></Suspense>} />
            <Route path="/isComing" element={<Suspense fallback={<PageFallback />}><ComingSoon /></Suspense>} />

            {/* Super admin only (add pages if needed) */}
            <Route element={<ProtectedRoute roles={['super_admin']} />}></Route>

            {/* Shared protected */}
            <Route element={<ProtectedRoute roles={['admin','user','user-facture','super_admin']} />}>
              <Route path="/Dashboard" element={<Layout><Suspense fallback={<PageFallback />}><Dashboard /></Suspense></Layout>} />
              <Route path="/bilan-carbon" element={<Layout><Suspense fallback={<PageFallback />}><BilanCarbon /></Suspense></Layout>} />
              <Route path="/bilan-carbon/dashboard" element={<Layout><Suspense fallback={<PageFallback />}><CarbonDashboard /></Suspense></Layout>} />
              <Route path="/bilan-carbon/analyse" element={<Layout><Suspense fallback={<PageFallback />}><CarbonAnalyse /></Suspense></Layout>} />
              <Route path="/bilan-carbon/solutions-action" element={<Layout><Suspense fallback={<PageFallback />}><CarbonSolutionsAction /></Suspense></Layout>} />
              <Route path="/Profile" element={<Layout><Suspense fallback={<PageFallback />}><Profile /></Suspense></Layout>} />
              <Route path="/Gestion-des-utilisateurs" element={<Layout><Suspense fallback={<PageFallback />}><GestionUsers /></Suspense></Layout>} />
              {/* GestionChauffeursMobile fusionné dans /Transport/Chauffeurs */}
            </Route>

            {/* Transport + Factures + Logistique */}
            <Route element={<ProtectedRoute roles={['admin','user-facture','super_admin','user']} />}>
              <Route path="/Transport/Tarification" element={<Layout><Suspense fallback={<PageFallback />}><Tarification /></Suspense></Layout>} />
              <Route path="/Transport/Voyages" element={<Layout><Suspense fallback={<PageFallback />}><Voyages /></Suspense></Layout>} />
              <Route path="/Transport/Chauffeurs" element={<Layout><Suspense fallback={<PageFallback />}><Chauffeurs /></Suspense></Layout>} />
              <Route path="/Transport/Vehicules" element={<Layout><Suspense fallback={<PageFallback />}><Vehicules /></Suspense></Layout>} />
              <Route path="/Transport/Fleet-Tracking" element={<Layout><Suspense fallback={<PageFallback />}><FleetTracking /></Suspense></Layout>} />
              <Route path="/Transport/Maintenance" element={<Layout><Suspense fallback={<PageFallback />}><Maintenance /></Suspense></Layout>} />
              <Route path="/Clients/ListClients" element={<Layout><Suspense fallback={<PageFallback />}><Clients /></Suspense></Layout>} />
              <Route path="/client-groups" element={<Layout><Suspense fallback={<PageFallback />}><ClientGroupsList /></Suspense></Layout>} />
              <Route path="/client-groups/:id" element={<Layout><Suspense fallback={<PageFallback />}><ClientGroupDetail /></Suspense></Layout>} />
              <Route path="/Carburants" element={<Layout><Suspense fallback={<PageFallback />}><Carburant /></Suspense></Layout>} />
              <Route path="/Logistique/Stock-scellés" element={<Layout><Suspense fallback={<PageFallback />}><StockScelles /></Suspense></Layout>} />

              <Route path="/factures" element={<Layout><Suspense fallback={<PageFallback />}><FacturesList /></Suspense></Layout>} />
              <Route path="/factures/view/:id" element={<Layout><Suspense fallback={<PageFallback />}><FactureView /></Suspense></Layout>} />
              <Route path="/factures/create" element={<Layout><Suspense fallback={<PageFallback />}><FactureForm /></Suspense></Layout>} />
              <Route path="/factures/edit/:id" element={<Layout><Suspense fallback={<PageFallback />}><FactureForm /></Suspense></Layout>} />
              
              {/* Documents */}
              <Route path="/Documents/Management" element={<Layout><Suspense fallback={<PageFallback />}><DocumentManagement /></Suspense></Layout>} />
              <Route path="/Documents/Statistics" element={<Layout><Suspense fallback={<PageFallback />}><DocumentStatistics /></Suspense></Layout>} />
            </Route>

            {/* Client Portal - Accès réservé aux utilisateurs avec rôle 'client' */}
            <Route element={<ProtectedRoute roles={['client']} />}>
              <Route path="/client-portal" element={<Suspense fallback={<PageFallback />}><ClientDashboard /></Suspense>} />
            </Route>

            {/* Fallback */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          <ToastContainer
            position="top-center"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
          />
        </ThemeProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
